import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  FileSearch,
  Upload,
  FileText,
  Check,
  X,
  AlertTriangle,
  Sparkles,
  Clock,
  CheckCircle,
  RefreshCw,
  ChevronRight,
  Edit3,
  Save,
  Trash2,
  Eye,
  Zap,
  Cpu,
  ScanText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Method badge
function MethodBadge({ method }) {
  const config = {
    pdf_text: { label: 'PDF Text', icon: FileText, color: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] bg-emerald-500/10 border-emerald-500/20' },
    ocr: { label: 'OCR Scan', icon: ScanText, color: 'text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] bg-blue-500/10 border-blue-500/20' },
    ai: { label: 'AI Extract', icon: Sparkles, color: 'text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] bg-purple-500/10 border-purple-500/20' },
    none: { label: 'Failed', icon: AlertTriangle, color: 'text-[#FF3B30] bg-red-500/10 border-red-500/20' }
  };
  const c = config[method] || config.none;
  const Icon = c.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border rounded ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// Confidence meter
function ConfidenceMeter({ confidence }) {
  const percent = Math.round(confidence * 100);
  const color = percent >= 80 ? 'bg-emerald-500' : percent >= 60 ? 'bg-amber-500' : 'bg-red-500';
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${percent}%` }} />
      </div>
      <span className="text-[10px] font-mono font-bold text-white/50">{percent}%</span>
    </div>
  );
}

// Extraction Preview Card
function ExtractionPreview({ data, onEdit, onApprove, onCancel, deals }) {
  const [editedData, setEditedData] = useState(data.extracted_data || {});
  const [selectedDeal, setSelectedDeal] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  const fields = [
    { key: 'property_address', label: 'Property Address', type: 'text' },
    { key: 'purchase_price', label: 'Purchase Price', type: 'currency' },
    { key: 'loan_amount', label: 'Loan Amount', type: 'currency' },
    { key: 'closing_date', label: 'Closing Date', type: 'text' },
    { key: 'buyer_name', label: 'Buyer Name', type: 'text' },
    { key: 'seller_name', label: 'Seller Name', type: 'text' },
    { key: 'lender_name', label: 'Lender', type: 'text' },
    { key: 'earnest_money', label: 'Earnest Money', type: 'currency' },
    { key: 'down_payment', label: 'Down Payment', type: 'currency' },
    { key: 'title_company', label: 'Title Company', type: 'text' },
  ];
  
  const handleFieldChange = (key, value) => {
    setEditedData({ ...editedData, [key]: value });
  };
  
  const handleApprove = () => {
    onApprove(data.document_id, editedData, selectedDeal || null);
  };
  
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#D4AF37]/20 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#D4AF37]/[0.08] rounded-lg flex items-center justify-center">
            <FileSearch className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Extracted Data</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <MethodBadge method={data.method} />
              <span className="text-[10px] text-white/40">Confidence:</span>
              <ConfidenceMeter confidence={data.confidence} />
            </div>
          </div>
        </div>
        
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-1 px-2 py-1 text-xs text-white/50 hover:text-white transition-all duration-300"
        >
          <Edit3 className="w-3 h-3" />
          {isEditing ? 'View' : 'Edit'}
        </button>
      </div>
      
      {/* Extracted Fields */}
      <div className="p-4 grid grid-cols-2 gap-4">
        {fields.map(field => {
          const value = editedData[field.key];
          const hasValue = value !== undefined && value !== null && value !== '';
          
          return (
            <div key={field.key} className="space-y-1">
              <Label className="text-[10px] text-white/40 uppercase tracking-wider">{field.label}</Label>
              {isEditing ? (
                <Input
                  value={value || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="h-8 text-sm bg-white/[0.02] border-white/[0.06] text-white"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                />
              ) : (
                <p className={`text-sm ${hasValue ? 'text-white' : 'text-white/30 italic'}`}>
                  {field.type === 'currency' && hasValue 
                    ? formatCurrency(value)
                    : hasValue ? value : 'Not found'}
                </p>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Apply to Deal */}
      <div className="px-4 py-3 bg-white/[0.01] border-t border-white/[0.06]">
        <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-2">
          Apply to Deal (Optional)
        </Label>
        <select
          value={selectedDeal}
          onChange={(e) => setSelectedDeal(e.target.value)}
          className="w-full h-9 bg-white/[0.02] border border-white/[0.06] rounded px-3 text-sm text-white"
        >
          <option value="">Don't apply to any deal</option>
          {deals.map(deal => (
            <option key={deal.deal_id} value={deal.deal_id}>
              {deal.address || deal.deal_id} - {deal.stage}
            </option>
          ))}
        </select>
      </div>
      
      {/* Actions */}
      <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-white/50 hover:text-white transition-all duration-300"
        >
          <X className="w-3 h-3" />
          Cancel
        </button>
        
        <Button
          onClick={handleApprove}
          className="bg-[#D4AF37] hover:bg-[#B8972E] text-black font-semibold"
        >
          <Check className="w-4 h-4 mr-2" />
          Approve & Save
        </Button>
      </div>
      
      {/* Errors */}
      {data.errors?.length > 0 && (
        <div className="px-4 py-2 bg-red-500/[0.05] border-t border-red-500/20">
          <p className="text-[10px] text-[#FF3B30] font-medium mb-1">Extraction Notes:</p>
          {data.errors.map((err, i) => (
            <p key={i} className="text-[10px] text-[#FF3B30]/70">{err}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// History Item
function HistoryItem({ doc, onView, onDelete }) {
  return (
    <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${
          doc.status === 'approved' ? 'bg-emerald-500/10' : 'bg-white/20/10'
        }`}>
          {doc.status === 'approved' 
            ? <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
            : <Clock className="w-4 h-4 text-white/50" />
          }
        </div>
        <div>
          <p className="text-sm text-white">{doc.filename}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <MethodBadge method={doc.extraction_method || 'none'} />
            <span className="text-[10px] text-white/40">
              {new Date(doc.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-1">
        <button
          onClick={() => onView(doc)}
          className="p-1.5 text-white/40 hover:text-white transition-all duration-300"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(doc.document_id)}
          className="p-1.5 text-white/40 hover:text-[#FF3B30] transition-all duration-300"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function DocumentIntelligencePage({ subscription }) {
  const { mainClass } = useSidebar();
  const fileInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [historyRes, dealsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/documents/history`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true })
      ]);
      setHistory(historyRes.data.documents || []);
      setDeals(dealsRes.data.deals || dealsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    
    setUploading(true);
    setExtractionResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'closing_statement');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/documents/extract`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setExtractionResult(response.data);
      
      if (response.data.success) {
        toast.success(`Extracted using ${response.data.method.replace('_', ' ')}`);
      } else {
        toast.warning('Could not extract data automatically');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };
  
  const handleApprove = async (documentId, data, dealId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/documents/approve`,
        { document_id: documentId, approved_data: data, deal_id: dealId },
        { withCredentials: true }
      );
      
      toast.success(dealId ? 'Data approved and applied to deal' : 'Data approved and saved');
      setExtractionResult(null);
      fetchData(); // Refresh history
    } catch (error) {
      toast.error('Failed to approve data');
    }
  };
  
  const handleDelete = async (documentId) => {
    if (!confirm('Delete this extraction?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/documents/${documentId}`, { withCredentials: true });
      toast.success('Document deleted');
      setHistory(history.filter(d => d.document_id !== documentId));
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar subscription={subscription} />
      
      <main className={mainClass}>
        {/* Header */}
        <div className="border-b border-white/[0.06] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <FileSearch className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">Document Intelligence</h1>
                <p className="text-xs text-white/40">Extract financial and legal data from contracts, HUDs, invoices, and closing documents.</p>
              </div>
            </div>
            
            {/* Method Legend */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <Zap className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                <span>Layer 1: PDF Text</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <ScanText className="w-3 h-3 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                <span>Layer 2: OCR</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <Sparkles className="w-3 h-3 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                <span>Layer 3: AI</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Main Area */}
            <div className="col-span-8 space-y-6">
              {/* Upload Zone */}
              {!extractionResult && (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                    dragOver 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/[0.05]' 
                      : 'border-white/[0.1] hover:border-white/[0.16] bg-white/[0.02]'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,image/png,image/jpeg,image/tiff"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    className="hidden"
                  />
                  
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mb-4" />
                      <p className="text-white/50">Processing document...</p>
                      <p className="text-xs text-white/30 mt-1">Trying PDF text → OCR → AI extraction</p>
                    </div>
                  ) : (
                    <>
                      <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? 'text-[#D4AF37]' : 'text-white/30'}`} />
                      <p className="text-white font-medium mb-1">Drop your document here</p>
                      <p className="text-sm text-white/40 mb-3">or click to browse</p>
                      <p className="text-xs text-white/30">Supports: PDF, PNG, JPEG, TIFF (max 10MB)</p>
                    </>
                  )}
                </div>
              )}
              
              {/* Extraction Result */}
              {extractionResult && (
                <ExtractionPreview
                  data={extractionResult}
                  deals={deals}
                  onApprove={handleApprove}
                  onCancel={() => setExtractionResult(null)}
                />
              )}
              
              {/* How It Works */}
              {!extractionResult && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-white mb-4">How It Works</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Zap className="w-5 h-5 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                      </div>
                      <p className="text-xs font-medium text-white">Layer 1: PDF Text</p>
                      <p className="text-[10px] text-white/40 mt-1">Fast, free extraction from digital PDFs</p>
                    </div>
                    <div className="text-center">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <ScanText className="w-5 h-5 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                      </div>
                      <p className="text-xs font-medium text-white">Layer 2: OCR</p>
                      <p className="text-[10px] text-white/40 mt-1">Scans images and scanned PDFs</p>
                    </div>
                    <div className="text-center">
                      <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Sparkles className="w-5 h-5 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                      </div>
                      <p className="text-xs font-medium text-white">Layer 3: AI</p>
                      <p className="text-[10px] text-white/40 mt-1">Smart extraction when other methods fail</p>
                    </div>
                  </div>
                </div>
              )}

              {/* What Can This Extract? */}
              {!extractionResult && (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-white mb-4">What Can This Extract?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { doc: "HUD Statements", desc: "Extract purchase price, closing costs, and settlement numbers", color: "#00FF88" },
                      { doc: "Contractor Invoices", desc: "Pull line items and costs into your rehab budget", color: "#0A84FF" },
                      { doc: "Rent Rolls", desc: "Analyze rental income across units and tenants", color: "#BF5AF2" },
                      { doc: "Loan Documents", desc: "Identify interest rates, terms, and payment schedules", color: "#D4AF37" },
                      { doc: "Title Documents", desc: "Extract ownership history and lien information", color: "#FF9F0A" },
                      { doc: "Closing Disclosures", desc: "Pull final transaction numbers and fee breakdowns", color: "#FF375F" },
                    ].map(item => (
                      <div key={item.doc} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/[0.04] rounded-lg">
                        <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: item.color, boxShadow: `0 0 8px ${item.color}60` }} />
                        <div>
                          <p className="text-xs font-semibold text-white">{item.doc}</p>
                          <p className="text-[10px] text-white/40 mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar - History */}
            <div className="col-span-4">
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg">
                <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-white uppercase tracking-wider">Recent Extractions</h3>
                  <span className="text-[10px] text-white/40">{history.length} docs</span>
                </div>
                
                <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
                    </div>
                  ) : history.length > 0 ? (
                    history.map(doc => (
                      <HistoryItem
                        key={doc.document_id}
                        doc={doc}
                        onView={(d) => setExtractionResult({ ...d, method: d.extraction_method })}
                        onDelete={handleDelete}
                      />
                    ))
                  ) : (
                    <p className="text-xs text-white/30 text-center py-8">No documents processed yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
